import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Zap,
  Play,
  Video,
  Film,
  Sparkles,
  Layers,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Building2,
  Tv,
  Monitor,
  Share2,
  PieChart,
  Eye,
  TrendingUp,
} from "lucide-react";

export const Route = createFileRoute("/services_/motion-graphics")({
  head: () => ({
    meta: [
      {
        title:
          "Motion Graphics Services — KMWAdvertising LLP | 2D/3D Animation & Explainer Videos",
      },
      {
        name: "description",
        content:
          "Bring Your Brand to Life with Engaging Motion Graphics Services by KMWAdvertising LLP. Logo animation, social motion creatives, explainer videos, kinetic typography, and OTT commercial animations.",
      },
      {
        property: "og:title",
        content: "Motion Graphics Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Capture audience attention in seconds with high-impact 2D/3D motion graphics, animated ads, kinetic typography, and corporate explainers.",
      },
    ],
  }),
  component: MotionGraphicsServicePage,
});

export function MotionGraphicsServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const motionServices = [
    "Brand Intro & Logo Animation",
    "Social Media Motion Creatives",
    "Animated Advertisements",
    "Corporate Explainer Videos",
    "Product Feature Animations",
    "Kinetic Typography Videos",
    "Infographics & Data Visualisation",
    "Event Openers & LED Screen Content",
    "Presentation Animations",
    "GIFs & Micro Animations",
    "Website Motion Graphics",
    "Digital Display & DOOH Animations",
    "YouTube Intro & Outro Animations",
    "Animated Reels & Shorts",
    "Motion Graphics for OTT & Television Commercials",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-rose-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Motion Graphics</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Zap className="h-3.5 w-3.5" />
              Motion Graphics & Animation
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Motion Graphics
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Bring Your Brand to Life with Engaging Motion Graphics
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                Static visuals capture attention, but motion creates impact. Motion graphics combine animation, typography, illustrations, icons and visual effects to simplify complex ideas, strengthen brand storytelling and improve audience engagement across digital platforms.
              </p>
              <p className="text-sm sm:text-base text-slate-400">
                Today's audiences consume content rapidly across social media, websites, OTT platforms and digital advertising. Professionally designed motion graphics help brands communicate messages more effectively while increasing watch time, click-through rates and brand recall.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                At KMWAdvertising LLP, we create visually compelling motion graphics that blend creativity with strategic communication. Every animation is designed to align with your brand identity and marketing objectives, ensuring consistency across all digital and traditional media channels.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#motion-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Start Motion Project</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#motion-services-list"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Motion Services</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Breakdown List */}
      <section id="motion-services-list" className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Sparkles className="h-3.5 w-3.5" />
              Full-Spectrum Animation Capabilities
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Our Motion Graphics Services Include:
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              From high-impact logo reveals and social video ads to complex 3D product explainers and television broadcast motion packages.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {motionServices.map((service, index) => (
              <div
                key={service}
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition flex items-center gap-3.5 group"
              >
                <div className="h-9 w-9 rounded-xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400 shrink-0 group-hover:scale-110 transition-transform">
                  <Play className="h-4 w-4 fill-pink-400" />
                </div>
                <div>
                  <span className="text-xs font-mono text-pink-400/80 block mb-0.5">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h4 className="text-sm font-bold text-white">{service}</h4>
                </div>
              </div>
            ))}
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-rose-950/20 to-purple-950/30 border border-white/15">
            <p className="text-slate-200 text-sm sm:text-base leading-relaxed font-medium">
              Whether you're launching a product, explaining a service or enhancing a marketing campaign, our motion graphics are designed to capture attention within the first few seconds and keep audiences engaged throughout.
            </p>
          </div>
        </div>
      </section>

      {/* Form & Contact */}
      <section id="motion-form" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Motion Brief Session
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Ready to Animate Your Brand Narrative?
              </h2>
              <p className="text-slate-300 text-sm">
                Share your campaign objectives or video script ideas with our motion directors for a custom animation production timeline.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Motion Brief Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our motion director will contact you within 24 hours to discuss storyboarding and production.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Arjun Mehta"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Horizon Labs"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="arjun@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Motion Graphic Type</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="explainer">Corporate Explainer Video</option>
                    <option value="logo">Logo Animation & Brand Intro</option>
                    <option value="social">Social Media Motion Ads / Reels</option>
                    <option value="tv">OTT & Broadcast TV Motion Graphics</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Script / Project Details</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about the message, duration, or key visual elements you want animated..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Request Motion Graphics Brief</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
